import React from "react";
import { Tooltip } from "@material-ui/core";
import {
  getSectorColors,
  getSectorEmoji,
  ACTION_CARD_HEADER_HEIGHT,
  ACTION_CARD_HEADER_OVERLAP
} from "../../utils/stockCardUtils";
import { useMobileDetection } from "../../utils/deviceDetection";
import CardBuySellOverlay from "./CardBuySellOverlay";
import MobileCardOverlay from "./MobileCardOverlay";
import OwnershipTokens from "./OwnershipTokens";
import StockCardTooltip, { PriceBreakdownTooltip } from "./StockCardTooltip";
import AppliedActionCardStack from "./AppliedActionCardStack";

const StockCardView = ({ stock = {}, indexes = [], styleCardSize = {}, onPurchase, onSell, canPurchase, canSell, ownedStock, stockOwnershipCounts = {}, stockOwnershipByPlayer = {}, playerPortfolios = {}, playerColors = {}, playerEmojis = {}, actionMode = null, selectedActionCard = null, roi = null, myTurn = false, roundNumber = 1, currentPlayerName = null, onForcedSale = null }) => {
  const name = stock.displayValue || stock.name || stock.Name || "Unnamed";
  const dividend =
    stock.dividend !== undefined ? stock.dividend : stock.div || 0;
  const growth = stock.growth !== undefined ? stock.growth : stock.g || 0;
  const baseCost =
    stock.baseCost !== undefined ? stock.baseCost : stock.base || stock.cost || 0;
  const sector = stock.industrySector || "";
  const sectorEmoji = getSectorEmoji(sector);
  const description = stock.description || "";
  
  // Check if title needs smaller font: 3 words and more than 18 characters
  const wordCount = name.trim().split(/\s+/).length;
  const needsSmallerFont = wordCount === 3 && name.length > 16;
  
  // Get colors for this sector
  const colors = getSectorColors(sector);

  // Use server-provided price breakdown (single source of truth)
  const breakdown = stock.priceBreakdown || {};
  const marketPrice = breakdown.marketPrice || (baseCost + (indexes.find(idx => idx.name === sector)?.price || 0) + (growth * (stockOwnershipCounts[name] || 0)));
  const previousPrice = breakdown.previousPrice;
  const priceChange = breakdown.priceChange || 0;
  const manipulatorEffect = breakdown.manipulatorEffect || 0;
  const stocksOwned = breakdown.stocksOwned || (stockOwnershipCounts[name] || 0);
  const indexPrice = breakdown.indexPrice || (indexes.find(idx => idx.name === sector)?.price || 0);
  const growthCost = breakdown.growthCost || (growth * stocksOwned);

  const handlePurchase = () => {
    if (onPurchase && canPurchase) {
      onPurchase(stock);
    }
  };

  const handleSell = () => {
    if (onSell && canSell && ownedStock) {
      onSell(ownedStock);
    }
  };

  const handleApplyAction = () => {
    if (!selectedActionCard || !actionMode) return;
    
    // Reset hover state immediately when action is applied
    setIsHovered(false);
    
    // Determine which handler to call based on action mode
    if (actionMode === "selecting_stock_up" || actionMode === "selecting_stock_buy" || actionMode === "selecting_stock_remove" || actionMode === "selecting_stock_ban" || actionMode === "selecting_double_down") {
      // Use purchase handler for "up" manipulations, insider trading, remove_manipulators, trading_halt, and double_down
      if (onPurchase) {
        onPurchase(stock);
      }
    } else if (actionMode === "selecting_stock_down") {
      // Use sell handler for "down" manipulations
      if (onSell) {
        onSell(stock);
      }
    } else if (actionMode === "selecting_rug_pull") {
      // For rug pull, use purchase handler (it will trigger the action card execution)
      if (onPurchase) {
        onPurchase(stock);
      }
    } else if (actionMode === "selecting_transfer_source" || actionMode === "selecting_transfer_target") {
      // Spin Cycle: source then target; both go through purchase handler
      if (onPurchase) {
        onPurchase(stock);
      }
    }
  };

  const [isHovered, setIsHovered] = React.useState(false);
  const [isNew, setIsNew] = React.useState(!stock.isCarryover); // New if not carryover
  const isMobile = useMobileDetection();
  const isCarryover = stock.isCarryover === true;
  const isOwned = ownedStock !== null && ownedStock !== undefined;
  
  // Remove "new" animation after it completes
  React.useEffect(() => {
    if (isNew) {
      const timer = setTimeout(() => {
        setIsNew(false);
      }, 2000); // Animation duration + buffer
      return () => clearTimeout(timer);
    }
  }, [isNew]);
  
  // Reset hover state when action mode is cleared (action was applied)
  React.useEffect(() => {
    if (!actionMode && !selectedActionCard) {
      setIsHovered(false);
    }
  }, [actionMode, selectedActionCard]);
  
  const appliedActionCards = stock.appliedActionCards || [];
  
  // Check if this card is an eligible target for selected action card
  const isEligibleTarget = selectedActionCard && actionMode && (
    actionMode === "selecting_stock_up" ||
    actionMode === "selecting_stock_down" ||
    actionMode === "selecting_stock_buy" ||
    actionMode === "selecting_stock_remove" ||
    actionMode === "selecting_stock_ban" ||
    (actionMode === "selecting_rug_pull" && isOwned) ||
    (actionMode === "selecting_double_down" && isOwned) ||
    actionMode === "selecting_transfer_source" ||
    actionMode === "selecting_transfer_target"
  );
  
  
  // Calculate total height needed for action card banners above
  const actionCardsHeight = appliedActionCards.length > 0 
    ? ACTION_CARD_HEADER_HEIGHT + (appliedActionCards.length - 1) * (ACTION_CARD_HEADER_HEIGHT - ACTION_CARD_HEADER_OVERLAP)
    : 0;

  // Use server-provided manipulator cards list
  const manipulatorCards = breakdown.manipulatorCards || [];
  
  const tooltipContent = (
    <StockCardTooltip
      name={name}
      sector={sector}
      sectorEmoji={sectorEmoji}
      dividend={dividend}
      isOwned={isOwned}
      isCarryover={isCarryover}
    />
  );

  const priceBreakdownContent = (
    <PriceBreakdownTooltip
      baseCost={baseCost}
      indexPrice={indexPrice}
      sectorEmoji={sectorEmoji}
      sector={sector}
      growthCost={growthCost}
      growth={growth}
      stocksOwned={stocksOwned}
      manipulatorEffect={manipulatorEffect}
      manipulatorCards={manipulatorCards}
      marketPrice={marketPrice}
      previousPrice={previousPrice}
    />
  );

  return (
    <div 
      className="stock-card-container" 
      style={{ 
        paddingTop: `${actionCardsHeight}px`
      }}
    >
      {/* Applied Action Cards Stack - Positioned Behind Stock Card */}
      <AppliedActionCardStack 
        appliedActionCards={appliedActionCards}
        styleCardSize={styleCardSize}
        playerColors={playerColors}
        playerEmojis={playerEmojis}
      />
      <Tooltip title={priceBreakdownContent} arrow placement="top">
      <div 
        className={`stock-card card ${isNew ? 'card-new' : ''} ${isEligibleTarget ? 'stock-card-eligible-target' : ''}`}
        style={{
          ...styleCardSize,
          background: colors.bg,
          ...(isEligibleTarget ? {} : { borderColor: colors.border })
        }}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <div 
          className="stock-card-top" 
          style={{ 
            background: colors.header,
            borderBottom: `2px solid ${colors.headerBorder}`
          }}
        >
          {sectorEmoji && (
            <span className="stock-card-top-emoji">
              {sectorEmoji}
            </span>
          )}
          <span className={`stock-card-top-name ${needsSmallerFont ? 'stock-card-top-name-small' : ''}`}>
            {name}
          </span>
        </div>
        
        {description && (
          <div className="stock-card-description">
            {description}
          </div>
        )}
        

        
        <div className="stock-card-bottom">
          <div className="stock-card-stats-row">
              <div className="stock-card-dividend" style={{ cursor: 'help' }}>🪙 {dividend}</div>
              <div className="stock-card-growth" style={{ cursor: 'help' }}>📈 {growth}x</div>
          </div>

            <div className="stock-card-cost-bar" data-price={marketPrice}>
              <span className="stock-card-price-value">{isMobile ? marketPrice : `💲${marketPrice}`}</span>
              {priceChange > 0 && (
                <span className="stock-card-price-change stock-card-price-change-positive">
                  ▲<span className="stock-card-price-change-value"> +{priceChange}</span>
                </span>
              )}
              {priceChange < 0 && (
                <span className="stock-card-price-change stock-card-price-change-negative">
                  ▼<span className="stock-card-price-change-value"> {priceChange}</span>
                </span>
              )}
            </div>

        </div>


        {/* Overlay - Mobile uses dialog, Desktop uses hover overlay */}
        {isMobile ? (
          <MobileCardOverlay
            cardName={name}
            onPurchase={handlePurchase}
            onSell={handleSell}
            canPurchase={canPurchase}
            canSell={canSell && ownedStock}
            buyPrice={marketPrice}
            sellPrice={Math.max(1, marketPrice - growth)}
            selectedActionCard={selectedActionCard}
            actionMode={actionMode}
            onApplyAction={handleApplyAction}
            roi={roi}
            tooltipContent={tooltipContent}
            description={description}
            isMyTurn={myTurn}
            priceBreakdownProps={{
              baseCost,
              indexPrice,
              sectorEmoji,
              sector,
              growthCost,
              growth,
              stocksOwned,
              manipulatorEffect,
              manipulatorCards,
              marketPrice
            }}
          />
        ) : (
        <CardBuySellOverlay
          isHovered={isHovered}
          onPurchase={handlePurchase}
          onSell={handleSell}
          canPurchase={canPurchase}
          canSell={canSell && ownedStock}
          buyPrice={marketPrice}
          sellPrice={Math.max(1, marketPrice - growth)}
          selectedActionCard={selectedActionCard}
          actionMode={actionMode}
          onApplyAction={handleApplyAction}
          roi={roi}
          isMyTurn={myTurn}
        />
        )}
      </div>
      </Tooltip>

      {/* Player ownership tokens - shown below the stock card */}
      <OwnershipTokens
        ownershipByPlayer={stockOwnershipByPlayer}
        playerPortfolios={playerPortfolios}
        itemName={stock.name}
        playerColors={playerColors}
        playerEmojis={playerEmojis}
        roundNumber={roundNumber}
        getPurchasePrice={(playerName, itemName, portfolio) => {
          const playerOwnedStock = portfolio.find(s => s.name === itemName);
          return playerOwnedStock?.purchasePrice;
        }}
        actionMode={actionMode}
        currentPlayerName={currentPlayerName}
        onForcedSaleClick={onForcedSale}
      />

    </div>
  );
};

export default StockCardView;